import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Loader2,
  Lock,
  Mail,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { track } from '../lib/posthog';
import DepartmentSelect from '../components/DepartmentSelect';
import { STANDARD_DEPARTMENTS } from '../data/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };

const TABS = [
  { id: 'branding', label: 'Organization' },
  { id: 'departments', label: 'Departments' },
  { id: 'team', label: 'HR Team' },
  { id: 'billing', label: 'Subscription & Billing' },
  { id: 'security', label: 'Security' },
];

const EMPTY_PERMS = {
  invite_hr: false,
  manage_org: false,
  manage_billing: false,
  manage_security: false,
  manage_departments: false,
};

const SIZE_OPTIONS = [
  { value: '', label: 'Select company size' },
  { value: '1-50', label: '1–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '501-1000', label: '501–1,000 employees' },
  { value: '1000+', label: '1,000+ employees' },
];

function CompanyLogoPreview({ name, logoUrl, previewFile }) {
  const src = previewFile ? URL.createObjectURL(previewFile) : logoUrl;
  const initial = (name || 'C').charAt(0).toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={`${name || 'Company'} logo`}
        className="h-20 w-20 object-contain rounded-xl border border-slate-200 bg-white p-1.5"
      />
    );
  }

  return (
    <div className="h-20 w-20 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center">
      <span className="text-2xl font-heading font-bold text-teal-600">{name ? initial : '—'}</span>
    </div>
  );
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { show, node } = useToast();
  const fileRef = useRef(null);

  const [activeTab, setActiveTab] = useState('branding');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [departmentChoices, setDepartmentChoices] = useState(STANDARD_DEPARTMENTS);
  const [newDept, setNewDept] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const [billing, setBilling] = useState(null);
  const [twoFa, setTwoFa] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordStep, setPasswordStep] = useState('form');
  const [otpCode, setOtpCode] = useState('');
  const [confirmingOtp, setConfirmingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  const [access, setAccess] = useState({ is_owner: false, permissions: EMPTY_PERMS });
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamInvites, setTeamInvites] = useState([]);
  const [permCatalog, setPermCatalog] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePerms, setInvitePerms] = useState(EMPTY_PERMS);
  const [inviting, setInviting] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { withCredentials: true });
      setCompanyName(data.organization?.company_name || '');
      setCompanySize(data.organization?.company_size || '');
      setLogoUrl(data.organization?.company_logo_url || null);
      setTwoFa(!!data.organization?.two_fa_enabled);
      setDepartments(data.departments || []);
      if (data.department_choices?.length) {
        setDepartmentChoices(data.department_choices);
      }
      setBilling(data.billing || null);
      if (data.access) setAccess(data.access);
      else if (data.user) {
        setAccess({
          is_owner: !!data.user.is_owner,
          permissions: data.user.permissions || EMPTY_PERMS,
        });
      }
    } catch {
      show('Failed to load settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [show]);

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/hr-team`, { withCredentials: true });
      setTeamMembers(data.members || []);
      setTeamInvites(data.invites || []);
      setPermCatalog(data.permission_catalog || []);
      setAccess((prev) => ({
        ...prev,
        is_owner: !!data.is_owner,
        permissions: data.permissions || prev.permissions,
      }));
    } catch {
      show('Failed to load HR team.', 'error');
    }
  }, [show]);

  useEffect(() => {
    loadSettings();
    loadTeam();
    track('hr_settings_viewed');
  }, [loadSettings, loadTeam]);

  const handleSaveBranding = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData();
    formData.append('company_name', companyName);
    formData.append('company_size', companySize);
    if (logoFile) formData.append('company_logo', logoFile);

    try {
      const { data } = await axios.patch(`${API}/settings`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(data.organization?.company_logo_url || null);
      setLogoFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await refreshUser();
      show('Organization settings saved.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to save organization settings.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDepartment = async () => {
    const name = newDept.trim();
    if (!name) return;
    try {
      const { data } = await axios.post(
        `${API}/settings/departments`,
        { name },
        { withCredentials: true },
      );
      setDepartments((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDept('');
      show(`Department "${name}" added.`);
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to add department.', 'error');
    }
  };

  const handleUpdateDepartment = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      const { data } = await axios.patch(
        `${API}/settings/departments/${id}`,
        { name },
        { withCredentials: true },
      );
      setDepartments((prev) =>
        prev.map((d) => (d.id === id ? data : d)).sort((a, b) => a.name.localeCompare(b.name)),
      );
      setEditingId(null);
      setEditingName('');
      show('Department updated.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to update department.', 'error');
    }
  };

  const perms = access.permissions || EMPTY_PERMS;
  const isOwner = !!access.is_owner;
  const canManageOrg = !!(isOwner || perms.manage_org);
  const canManageDepartments = !!(isOwner || perms.manage_departments);
  const canManageBilling = !!(isOwner || perms.manage_billing || billing?.can_manage);
  const canManageSecurity = !!(isOwner || perms.manage_security);
  const canManageTeam = !!(isOwner || perms.invite_hr);

  const toggleInvitePerm = (id) => {
    setInvitePerms((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleInviteHr = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await axios.post(
        `${API}/settings/hr-team/invite`,
        { email: inviteEmail.trim(), permissions: invitePerms },
        { withCredentials: true },
      );
      setInviteEmail('');
      setInvitePerms(EMPTY_PERMS);
      await loadTeam();
      show('Invite sent. They will get an email to join your company.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to send invite.', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (id) => {
    try {
      await axios.post(`${API}/settings/hr-team/invites/${id}/resend`, {}, { withCredentials: true });
      await loadTeam();
      show('Invite sent again.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to resend invite.', 'error');
    }
  };

  const handleCancelInvite = async (id) => {
    try {
      await axios.delete(`${API}/settings/hr-team/invites/${id}`, { withCredentials: true });
      await loadTeam();
      show('Invite cancelled.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to cancel invite.', 'error');
    }
  };

  const handleUpdateMemberPerms = async (member, nextPerms) => {
    try {
      await axios.patch(
        `${API}/settings/hr-team/members/${member.id}`,
        { permissions: nextPerms },
        { withCredentials: true },
      );
      await loadTeam();
      show(`Permissions updated for ${member.name || member.email}.`);
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to update permissions.', 'error');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.name || member.email} from this company?`)) return;
    try {
      await axios.delete(`${API}/settings/hr-team/members/${member.id}`, { withCredentials: true });
      await loadTeam();
      show('HR teammate removed.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to remove teammate.', 'error');
    }
  };

  const handleDeleteDepartment = async (id, name) => {
    if (!window.confirm(`Delete department "${name}"?`)) return;
    try {
      await axios.delete(`${API}/settings/departments/${id}`, { withCredentials: true });
      setDepartments((prev) => prev.filter((d) => d.id !== id));
      show('Department deleted.');
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to delete department.', 'error');
    }
  };

  const handleToggle2FA = async () => {
    const next = !twoFa;
    try {
      await axios.patch(
        `${API}/settings`,
        { two_fa_enabled: next },
        { withCredentials: true },
      );
      setTwoFa(next);
      await refreshUser();
      show(next ? 'Two-factor authentication enabled (mock).' : 'Two-factor authentication disabled.');
    } catch {
      show('Failed to update 2FA setting.', 'error');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      show('New passwords do not match.', 'error');
      return;
    }
    setChangingPassword(true);
    try {
      const { data } = await axios.post(
        `${API}/settings/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      if (data.requires_verification) {
        setPasswordStep('otp');
        setOtpCode('');
        show(data.message || 'Verification code sent to your email.');
      } else {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        show(data.message || 'Password updated successfully.');
      }
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to change password.', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleConfirmPasswordOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpCode.trim())) {
      show('Enter the 6-digit code from your email.', 'error');
      return;
    }
    setConfirmingOtp(true);
    try {
      const { data } = await axios.post(
        `${API}/settings/change-password/confirm`,
        { code: otpCode.trim() },
        { withCredentials: true },
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpCode('');
      setPasswordStep('form');
      show(data.message || 'Password updated successfully.');
    } catch (err) {
      show(err.response?.data?.detail || 'Could not verify the code.', 'error');
    } finally {
      setConfirmingOtp(false);
    }
  };

  const handleResendPasswordOtp = async () => {
    if (!currentPassword || !newPassword) {
      setPasswordStep('form');
      show('Enter your current and new password again to resend the code.', 'error');
      return;
    }
    setResendingOtp(true);
    try {
      const { data } = await axios.post(
        `${API}/settings/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      setOtpCode('');
      show(data.message || 'A new code was sent to your email.');
    } catch (err) {
      show(err.response?.data?.detail || 'Could not resend the code.', 'error');
    } finally {
      setResendingOtp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      {node}

      <motion.div initial="hidden" animate="visible" variants={fadeUp}>
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500 mt-1">
            Manage your organization, HR team, departments, billing, and security.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              data-testid={`settings-tab-${id}`}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === id
                  ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/25'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-200 hover:text-teal-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="glass-card rounded-2xl border border-slate-200/80 p-6 md:p-8">
          {/* TAB 1: Branding */}
          {activeTab === 'branding' && (
            <form onSubmit={handleSaveBranding} className="space-y-6">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">
                  Organization
                </h2>
                <p className="text-sm text-slate-500">
                  Your logo appears in the dashboard sidebar and employee-facing surveys.
                  {!canManageOrg && (
                    <span className="block mt-1 text-amber-700">
                      View only — ask HR Head if you need to change branding.
                    </span>
                  )}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <CompanyLogoPreview name={companyName} logoUrl={logoUrl} previewFile={logoFile} />
                <div className="flex-1 space-y-3">
                  <label className="block text-sm font-medium text-slate-700">Company Logo</label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="logo-upload"
                    disabled={!canManageOrg}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-teal-300 hover:text-teal-700 cursor-pointer transition-colors ${!canManageOrg ? 'pointer-events-none opacity-50' : ''}`}
                  >
                    <Upload className="w-4 h-4" /> Upload logo
                  </label>
                  <p className="text-xs text-slate-400">PNG, JPG, WebP or SVG. Recommended 256×256px.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Acme Corp"
                    disabled={!canManageOrg}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Size</label>
                  <select
                    value={companySize}
                    onChange={(e) => setCompanySize(e.target.value)}
                    disabled={!canManageOrg}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
                  >
                    {SIZE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Button type="submit" disabled={saving || !canManageOrg} className="bg-teal-600 hover:bg-teal-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </form>
          )}

          {/* TAB 2: Departments */}
          {activeTab === 'departments' && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">
                  Departments
                </h2>
                <p className="text-sm text-slate-500">
                  Departments power the Anonymity Firewall — analytics only unlock when 2+ people in a
                  department respond.
                  {!canManageDepartments && (
                    <span className="block mt-1 text-amber-700">
                      View only — ask HR Head to grant department edit rights.
                    </span>
                  )}
                </p>
              </div>

              <div className={`space-y-3 ${!canManageDepartments ? 'opacity-60 pointer-events-none' : ''}`}>
                <DepartmentSelect
                  id="settings-new-department"
                  label="Add department"
                  value={newDept}
                  onChange={setNewDept}
                  choices={departmentChoices}
                  companyDepartments={[]}
                  data-testid="settings-new-department"
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleAddDepartment}
                    disabled={!newDept.trim()}
                    className="bg-teal-600 hover:bg-teal-700 shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <p className="text-xs text-slate-400">
                  Choose from the enterprise catalog or select Other / Custom Department to type your own.
                </p>
              </div>

              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                {departments.length === 0 ? (
                  <li className="px-4 py-8 text-center text-sm text-slate-400">
                    No departments yet. Add your first one above.
                  </li>
                ) : (
                  departments.map((dept) => (
                    <li
                      key={dept.id}
                      className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-slate-50/80"
                    >
                      {editingId === dept.id ? (
                        <>
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="flex-1"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateDepartment(dept.id)}
                            className="bg-teal-600 hover:bg-teal-700"
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingId(null);
                              setEditingName('');
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-slate-800">{dept.name}</span>
                          {canManageDepartments && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(dept.id);
                                setEditingName(dept.name);
                              }}
                              className="p-2 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50"
                              aria-label={`Edit ${dept.name}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                              aria-label={`Delete ${dept.name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          )}
                        </>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">HR Team</h2>
                <p className="text-sm text-slate-500">
                  Everyone here shares this company&apos;s dashboard — employees, surveys, and analytics.
                  HR Head invites teammates and can grant extra rights such as billing or inviting others.
                </p>
              </div>

              {canManageTeam ? (
                <form onSubmit={handleInviteHr} className="rounded-xl border border-slate-200 p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-teal-600" /> Invite HR from your company
                  </h3>
                  <Input
                    type="email"
                    placeholder="hr.teammate@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                  />
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Extra permissions (optional)
                    </p>
                    <p className="text-xs text-slate-400">
                      Invited HRs can always import employees, run surveys, and view the dashboard, departments, and organization.
                    </p>
                    {(permCatalog.length ? permCatalog : [
                      { id: 'invite_hr', label: 'Invite and remove HR teammates' },
                      { id: 'manage_org', label: 'Edit company name, logo, and size' },
                      { id: 'manage_billing', label: 'Manage subscription and billing' },
                      { id: 'manage_security', label: 'Manage company security (2FA)' },
                      { id: 'manage_departments', label: 'Add, edit, and delete departments' },
                    ]).map((item) => (
                      <label key={item.id} className="flex items-start gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-slate-300 text-teal-600"
                          checked={!!invitePerms[item.id]}
                          onChange={() => toggleInvitePerm(item.id)}
                        />
                        <span>
                          {item.label}
                          {item.help && <span className="block text-xs text-slate-400">{item.help}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                  <Button type="submit" disabled={inviting} className="bg-teal-600 hover:bg-teal-700">
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send invite
                  </Button>
                </form>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                  Only HR Head, or someone they granted invite rights to, can add or remove teammates.
                </p>
              )}

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">People on this account</h3>
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                  {teamMembers.map((member) => (
                    <li key={member.id} className="px-4 py-4 bg-white space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-800">
                            {member.name || member.email}
                            {member.is_owner && (
                              <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                HR Head
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                        {canManageTeam && !member.is_owner && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member)}
                            className="text-xs font-medium text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {!member.is_owner && canManageTeam && (
                        <div className="grid sm:grid-cols-2 gap-2">
                          {(permCatalog.length ? permCatalog : Object.keys(EMPTY_PERMS).map((id) => ({ id, label: id }))).map((item) => (
                            <label key={item.id} className="flex items-center gap-2 text-xs text-slate-600">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 text-teal-600"
                                checked={!!member.permissions?.[item.id]}
                                onChange={() =>
                                  handleUpdateMemberPerms(member, {
                                    ...member.permissions,
                                    [item.id]: !member.permissions?.[item.id],
                                  })
                                }
                              />
                              {item.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {teamInvites.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Pending invites</h3>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                    {teamInvites.map((invite) => (
                      <li key={invite.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-white">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{invite.email}</p>
                          <p className="text-xs text-slate-400">
                            Invited by {invite.invited_by_name || 'HR Head'}
                          </p>
                        </div>
                        {canManageTeam && (
                          <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline" onClick={() => handleResendInvite(invite.id)}>
                              Resend
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={() => handleCancelInvite(invite.id)}>
                              Cancel
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Billing */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">
                  Subscription & Billing
                </h2>
                <p className="text-sm text-slate-500">
                  Your quarterly plan and invoice history.
                  {!canManageBilling && (
                    <span className="block mt-1 text-amber-700">
                      Billing is managed by HR Head unless they grant you this right.
                    </span>
                  )}
                </p>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-slate-900 to-teal-950 p-6 text-white">
                <p className="text-teal-300 text-sm font-medium mb-1">Active Plan</p>
                <p className="font-heading text-xl font-semibold">
                  {billing?.subscription_active
                    ? billing.plan_label
                    : 'No active subscription'}
                </p>
                {billing?.subscription_active && (
                  <div className="mt-4 flex flex-wrap gap-6 text-sm text-slate-300">
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Next payment</p>
                      <p className="text-white font-medium">{billing.next_payment_date}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-wide">Billing cycle</p>
                      <p className="text-white font-medium">{billing.billing_cycle}</p>
                    </div>
                  </div>
                )}
                {!billing?.subscription_active && (
                  <p className="mt-3 text-sm text-slate-400">
                    Visit Pricing to activate a quarterly plan and unlock your dashboard.
                  </p>
                )}
                {canManageBilling && (
                <Link to="/pricing">
                  <Button className="mt-5 bg-white text-teal-800 hover:bg-teal-50 font-semibold">
                    {billing?.subscription_plan && billing.subscription_plan !== 'free'
                      ? 'Manage plan'
                      : 'Upgrade plan'}
                  </Button>
                </Link>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Previous Invoices</h3>
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">Invoice</th>
                        <th className="text-left px-4 py-2.5 font-medium">Date</th>
                        <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(billing?.invoices || []).map((inv) => (
                        <tr key={inv.id} className="bg-white">
                          <td className="px-4 py-3 font-medium text-slate-800">{inv.id}</td>
                          <td className="px-4 py-3 text-slate-600">{inv.date}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: Security */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1">Security</h2>
                <p className="text-sm text-slate-500">
                  Protect your HR account with a strong password and two-factor authentication.
                </p>
              </div>

              {passwordStep === 'otp' ? (
                <form onSubmit={handleConfirmPasswordOtp} className="space-y-4 max-w-md">
                  <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-teal-600" /> Verify password change
                  </h3>
                  <p className="text-sm text-slate-500">
                    We sent a 6-digit code to{' '}
                    <span className="font-medium text-slate-700">{user?.email}</span>.
                    Enter it below to finish changing your password. Check spam if it is not in inbox.
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    className="tracking-[0.4em] text-center text-lg font-semibold"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      disabled={confirmingOtp || otpCode.length !== 6}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {confirmingOtp ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Lock className="w-4 h-4 mr-2" />
                      )}
                      Confirm &amp; Update Password
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={resendingOtp}
                      onClick={handleResendPasswordOtp}
                    >
                      {resendingOtp && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Resend code
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setPasswordStep('form');
                        setOtpCode('');
                      }}
                    >
                      Back
                    </Button>
                  </div>
                </form>
              ) : (
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-teal-600" /> Change Password
                </h3>
                <p className="text-xs text-slate-500">
                  We will email a verification code to {user?.email || 'your account'} before the new password is saved.
                </p>
                <Input
                  type="password"
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <Input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Send verification code
                </Button>
              </form>
              )}

              <div className="pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between gap-4 max-w-md">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Two-Factor Authentication</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Enterprise-grade security for your HR account (mock toggle).
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={twoFa}
                    onClick={canManageSecurity ? handleToggle2FA : undefined}
                    disabled={!canManageSecurity}
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      twoFa ? 'bg-teal-600' : 'bg-slate-200'
                    } ${!canManageSecurity ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                        twoFa ? 'translate-x-5' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
